import React, { useState, useRef, useEffect } from "react";
import { Card, Input, Label, Table } from "reactstrap";
import { PlusCircle, SearchIcon } from "lucide-react";
import SearchFilterButtons from "../../SearchFilterButtons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { deleteParameter, getParameter, statusParameter } from "./service/parameter-service";
import MyPagination from "../../pagination";
import Swal from "sweetalert2";
import { useLoader } from "../../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { FormLabel } from "react-bootstrap";
import { Tooltip } from "react-tooltip";

export default function HomeReport() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const { setLoading } = useLoader();

    useEffect(() => {
        getParamete();
    }, [currentPage, entriesPerPage]);

    const getParamete = async (query = appliedSearch) => {
        setLoading(true);
        try {
            const form_data = new FormData();
            form_data.append("page", currentPage);
            form_data.append("limit", entriesPerPage);
            form_data.append("search", query);
            const res = await getParameter(form_data);
            if (res.data.success === true) {
                setUsers(res.data.data.data);
                setTotalEntries(res.data.data.total || 0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        const query = search.trim();
        setAppliedSearch(query);
        if (currentPage === 1) {
            getParamete(query);
        } else {
            setCurrentPage(1);
        }
    };

    const handleClear = () => {
        setSearch("");
        if (!appliedSearch) return;
        setAppliedSearch("");
        if (currentPage === 1) {
            getParamete("");
        } else {
            setCurrentPage(1);
        }
    };

    const totalPages = Math.ceil(totalEntries / entriesPerPage);

    const handleToggleStatus = (data) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: data.isActive
                ? t("You want to inactivate this parameter?")
                : t("You want to activate this parameter?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: data.isActive ? t("Inactive") : t("Active")
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await statusParameter(data._id);
                    if (res.data.success == true) {
                        toast.success(res.data.message);
                        getParamete();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleEdit = (data) => {
        navigate(`${process.env.PUBLIC_URL}/edit-parameter/${data._id}`);
    };

    const detele = (data) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: t("You want to delete this parameter?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t("Yes, Delete It!")
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await deleteParameter(data._id);
                    if (res.data.success == true) {
                        toast.success(res.data.message);
                        getParamete();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setEntriesPerPage(newSize);
        setCurrentPage(1);
    };

    const getStartingSerialNumber = () => {
        return (currentPage - 1) * entriesPerPage + 1;
    };

    // Helper to format a single range value
    const formatRangeValue = (range) => {
        if (range.minValue != null && range.maxValue != null) {
            return `${range.minValue} - ${range.maxValue}`;
        }
        return range.qualitativeValue || range.ratioValue || range.scaleValue || "N/A";
    };

    // Show all reference ranges in a popup
    const showAllRanges = (ranges) => {
        const rangeList = ranges
            ?.map((r, idx) => {
                const value = formatRangeValue(r);
                const gender = r.gender === "all" ? "All" : r.gender?.charAt(0).toUpperCase() + r.gender?.slice(1);
                const ageCategory = r.ageCategory
                    ? r.ageCategory.charAt(0).toUpperCase() + r.ageCategory.slice(1)
                    : "N/A";

                return `
                    <div class="reference-range-card tw-text-left tw-bg-gray-50 tw-rounded-md tw-border tw-border-gray-200/70">
                        <div class="tw-mb-0.5 tw-flex tw-items-baseline tw-gap-1.5 tw-flex-wrap">
                            <strong class="tw-text-[14px] tw-font-semibold tw-text-gray-700">${t("Range")} ${idx + 1}:</strong>
                            <span class="reference-range-value tw-text-[15px] tw-font-bold tw-text-gray-900">${value}</span>
                        </div>
                        <div class="reference-range-details tw-text-[13px] tw-text-gray-600 tw-flex tw-items-center tw-flex-wrap tw-gap-x-2 tw-gap-y-0.5">
                            <span>${t("Gender")}: <span class="tw-font-medium tw-text-gray-800">${gender}</span></span>
                            <span class="tw-text-gray-300">|</span>
                            <span>${t("Age Category")}: <span class="tw-font-medium tw-text-gray-800">${ageCategory}</span></span>
                        </div>
                    </div>
                `;
            })
            .join("<hr class='reference-range-divider tw-my-1 tw-border-gray-200'/>");

        Swal.fire({
            title: `<strong>${t("All Reference Ranges")}</strong>`,
            html: rangeList || `<div class="tw-text-gray-500 tw-py-2">${t("No ranges defined")}</div>`,
            confirmButtonText: t("Close"),
            width: "calc(100% - 32px)",
            customClass: {
                popup: "swal-custom-popup reference-ranges-popup",
                title: "reference-ranges-title",
                htmlContainer: "reference-ranges-body",
                actions: "reference-ranges-actions",
                confirmButton: "reference-ranges-confirm-btn"
            }
        });
    };

    // Format display for Reference Ranges column
    const formatReferenceRangeDisplay = (ranges) => {
        if (!ranges || ranges.length === 0) return "N/A";

        const firstRangeValue = formatRangeValue(ranges[0]);

        if (ranges.length === 1) {
            return <span>{firstRangeValue}</span>;
        }

        return (
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <span>{firstRangeValue}</span>
                <button
                    className="tw-cursor-pointer tw-border tw-text-black tw-border-[#006E27] tw-rounded tw-px-2 tw-py-1"
                    onClick={(e) => {
                        e.stopPropagation();
                        showAllRanges(ranges);
                    }}
                >
                    +{ranges.length - 1} {t("more")}
                </button>
            </div>
        );
    };

    return (
        <>
            <Card>
                <div className="px-3 mt-4">
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        <div className="input-group" style={{ flex: "1 1 260px", maxWidth: 460 }}>
                            <span className="input-group-text tw-bg-white" id="basic-addon1">
                                <SearchIcon />
                            </span>
                            <input
                                type="text"
                                className="form-control icon_form"
                                placeholder={t("Search by name and code")}
                                aria-label={t("Search by name and code")}
                                aria-describedby="basic-addon1"
                                style={{ borderLeft: 'none' }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                            />
                        </div>
                        <SearchFilterButtons onSearch={handleSearch} onClear={handleClear} />
                        <div className="ms-md-auto">
                            <Link to={`${process.env.PUBLIC_URL}/add-parameter`}>
                                <button className="btn btn-primary">
                                    {t("Add")}
                                </button>
                            </Link>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <div className="recent-table table-responsive currency-table tw-rounded-lg tw-border tw-border-black custom-slim-scrollbar" style={{ overflowX: "auto" }}>
                                <Table className="mb-0 table-center table-bordered table-hover doctor-table" style={{ tableLayout: "fixed", overflowX: "auto" }}>
                                    <thead>
                                        <tr>
                                            <th className="text-center bg-primary tw-text-center" style={{ width: "50px", whiteSpace: "nowrap" }}><b>#</b></th>
                                            <th className="bg-primary text-white" style={{ width: "85px", whiteSpace: "nowrap" }}><b>{t("Code")}</b></th>
                                            <th className="bg-primary text-white" style={{ width: "225px", whiteSpace: "nowrap" }}><b>{t("Name")}</b></th>
                                            <th className="bg-primary text-white" style={{ width: "150px", whiteSpace: "nowrap" }}><b>{t("Measurement Type")}</b></th>
                                            <th className="bg-primary text-white" style={{ width: "170px", whiteSpace: "nowrap" }}><b>{t("Reference Ranges")}</b></th>
                                            <th className="bg-primary text-white" style={{ width: "70px", whiteSpace: "nowrap" }}><b>{t("Unit")}</b></th>
                                            <th className="bg-primary text-white text-center" style={{ width: "80px", whiteSpace: "nowrap" }}><b>{t("Status")}</b></th>
                                            <th className="bg-primary text-white text-center" style={{ width: "90px", whiteSpace: "nowrap" }}><b>{t("Action")}</b></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length > 0 ? (
                                            users.map((item, index) => {
                                                const serialNumber = getStartingSerialNumber() + index;
                                                return (
                                                    <tr key={item._id}>
                                                        <td className="text-center font-weight-bold">{serialNumber}</td>
                                                        <td>{item.code}</td>
                                                        <td>{item.name}</td>
                                                        <td>{item.measurementType}</td>
                                                        <td>
                                                            {formatReferenceRangeDisplay(item.referenceRanges)}
                                                        </td>
                                                        <td>{item.unitValue || "-"}</td>
                                                        <td className="text-center">
                                                            <Label className="switch doctor-switch">
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={item.isActive === true}
                                                                    onChange={() => handleToggleStatus(item)}
                                                                />
                                                                <span className="switch-state"></span>
                                                            </Label>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-inline-flex align-items-center justify-content-center">
                                                                <Tooltip id="edit" place="top" content={t("Edit")} />
                                                                <button
                                                                    className="success-action-button me-1"
                                                                    data-tooltip-id="edit"
                                                                    onClick={() => handleEdit(item)}
                                                                >
                                                                    <i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i>
                                                                </button>

                                                                <Tooltip id="delete" place="top" content={t("Delete")} />
                                                                <button
                                                                    className="danger-action-button"
                                                                    data-tooltip-id="delete"
                                                                    onClick={() => detele(item)}
                                                                >
                                                                    <i className="fa fa-trash tw-text-red-600" style={{ fontSize: "18px" }}></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={8} className="text-center py-4 text-muted">
                                                    {t("No data available")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-between gap-2 mt-2 mb-3 pt-1">
                                <div className="show-entries d-inline-flex align-items-center">
                                    <FormLabel className="mb-0 text-muted" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                                        {t("Show")}&nbsp;
                                    </FormLabel>
                                    <select
                                        style={{
                                            display: "inline-block",
                                            width: "auto",
                                            height: "28px",
                                            padding: "0.15rem 1.6rem 0.15rem 0.5rem",
                                            fontSize: "12px",
                                            lineHeight: 1.2,
                                            margin: "0 4px"
                                        }}
                                        value={entriesPerPage}
                                        onChange={handlePageSizeChange}
                                        className="form-control form-control-sm"
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={30}>30</option>
                                        <option value={50}>50</option>
                                    </select>
                                    <FormLabel className="mb-0 text-muted" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                                        {t("entries")}
                                    </FormLabel>
                                </div>
                                <div className="d-flex justify-content-center justify-content-sm-end overflow-auto" style={{ maxWidth: "100%" }}>
                                    <MyPagination
                                        totalPages={totalPages}
                                        currentPage={currentPage}
                                        onPageChange={(page) => setCurrentPage(page)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </>
    );
}